import type { Language } from '@/types/model';

export const languageCodes: Record<Language, string[]> = {
  GERMAN: ['de', 'deu', 'ger'],
  ENGLISH: ['en', 'eng'],
  KOREAN: ['ko', 'kor'],
  SPANISH: ['es', 'spa'],
  FRENCH: ['fr', 'fra', 'fre'],
  ITALIAN: ['it', 'ita'],
  JAPANESE: ['ja', 'jpn'],
  MANDARIN: ['zh', 'zho', 'chi', 'cmn'],
  PORTUGUESE: ['pt', 'por'],
  THAI: ['th', 'tha'],
  CATALAN: ['ca', 'cat'],
  RUSSIAN: ['ru', 'rus'],
  ARABIC: ['ar', 'ara'],
  HINDI: ['hi', 'hin'],
  TURKISH: ['tr', 'tur'],
  DUTCH: ['nl', 'nld', 'dut'],
  POLISH: ['pl', 'pol'],
  SWEDISH: ['sv', 'swe'],
  NORWEGIAN: ['no', 'nor'],
  DANISH: ['da', 'dan'],
  FINNISH: ['fi', 'fin'],
  GREEK: ['el', 'ell', 'gre'],
  HEBREW: ['he', 'heb'],
  BULGARIAN: ['bg', 'bul'],
  CZECH: ['cs', 'ces', 'cze'],
  HUNGARIAN: ['hu', 'hun'],
  ROMANIAN: ['ro', 'ron', 'rum'],
};

interface TrackDescriptor {
  language?: string;
  label?: string;
}

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
const displayNames = ['en', 'es'].map((locale) => new Intl.DisplayNames([locale], { type: 'language' }));
const languageAliases = Object.entries(languageCodes).map(([language, codes]) => ({
  language: language as Language,
  aliases: [language, ...codes, ...displayNames.map((names) => names.of(codes[0]) ?? '')]
    .filter(Boolean).map(normalize),
}));

const identifyLanguage = (value: string) => {
  const normalized = normalize(value);
  return languageAliases.find(({ aliases }) => aliases.some((alias) => (
    normalized === alias || normalized.startsWith(`${alias}-`) || normalized.startsWith(`${alias} `)
    || normalized.startsWith(`${alias} (`)
  )))?.language;
};

export const trackLanguage = (track: TrackDescriptor) => (
  identifyLanguage(track.language ?? '') ?? identifyLanguage(track.label ?? '')
);

export const samePlaybackSource = (first: string, second: string): boolean => {
  if (first === second) return true;
  const identity = (value: string) => {
    const url = new URL(value);
    // A refreshed storage signature does not identify a different media file.
    const signingKeys = Array.from(url.searchParams.keys()).filter((key) => (
      key === 'exp' || key === 'sig' || key.toLowerCase().startsWith('x-amz-')
    ));
    signingKeys.forEach((key) => url.searchParams.delete(key));
    url.searchParams.sort();
    return url.href;
  };
  try { return identity(first) === identity(second); } catch { return false; }
};
